import { HardwareOrderStatus } from "interfaces";

/**
 * Maps HAM statuses to their AMZNHAM equivalents.
 * Used when querying the AMZNHAM consumer to ensure all relevant orders are returned.
 * HAM statuses that map directly (same value in both systems) are not included here.
 */
export const HAM_TO_AMZNHAM_STATUS_MAP: Record<string, string[]> = {
  [HardwareOrderStatus.ASSIGNED]: [HardwareOrderStatus.PROCESSING],
  [HardwareOrderStatus.WORK_IN_PROGRESS]: [HardwareOrderStatus.PROCESSING],
  [HardwareOrderStatus.PENDING_QUOTE]: [HardwareOrderStatus.PENDING_APPROVAL],
  [HardwareOrderStatus.PENDING_REQ_INFO]: [HardwareOrderStatus.PENDING_APPROVAL],
  [HardwareOrderStatus.PENDING_DELIVERY]: [HardwareOrderStatus.PENDING_SHIPPING, HardwareOrderStatus.SHIPPED],
};

/**
 * Expands a list of HAM statuses to include their AMZNHAM equivalents.
 * Used on the support tab Amazonian subtab to query the AMZNHAM consumer with all relevant statuses.
 */
export const expandStatusesForAmznham = (statuses: string[]): string[] => {
  const expanded = new Set(statuses);
  for (const status of statuses) {
    const mapped = HAM_TO_AMZNHAM_STATUS_MAP[status];
    if (mapped) {
      mapped.forEach(s => expanded.add(s));
    }
  }
  return Array.from(expanded);
};

/**
 * Represents the selectable status options used to filter hardware orders.
 *
 * The statuses are grouped into two categories:
 * - Open Status: Orders that are currently being processed or are awaiting further actions.
 * - Complete Status: Orders that have reached a final state (fulfilled, cancelled, or rejected).
 *
 * @constant
 * @type {Array<{ label: string; options: Array<{ label: string; value: HardwareOrderStatus }> }>}
 */
export const STATUS_OPTIONS = [
  {
    label: "Open Status",
    options: [
      { label: "New", value: HardwareOrderStatus.NEW },
      { label: "Approved", value: HardwareOrderStatus.APPROVED },
      { label: "Assigned", value: HardwareOrderStatus.ASSIGNED },
      { label: "Work In Progress", value: HardwareOrderStatus.WORK_IN_PROGRESS },
      { label: "Pending Quote", value: HardwareOrderStatus.PENDING_QUOTE },
      { label: "Pending Requester Info", value: HardwareOrderStatus.PENDING_REQ_INFO },
      { label: "Pending Approval", value: HardwareOrderStatus.PENDING_APPROVAL },
      { label: "Pending Delivery", value: HardwareOrderStatus.PENDING_DELIVERY },
    ]
  },
  {
    label: "Complete Status",
    options: [
      { label: "Cancelled", value: HardwareOrderStatus.CANCELLED },
      { label: "Delivered", value: HardwareOrderStatus.DELIVERED },
      { label: "Rejected", value: HardwareOrderStatus.REJECTED }
    ]
  }
];